import { isJsonApiRequest } from '@efdigital/ll-admin-v2/lib/json-api.ts';
import type { Handler, NextFn } from '@warp-drive/core/request';
import type {
  RequestContext,
  StructuredDataDocument,
} from '@warp-drive/core/types/request';

import type { JsonApiDocument } from '../normalizers/jsonapi-document.ts';
import {
  NormalizeCamelCaseToDasherize,
  NormalizeDasherizeToCamelCase,
} from '../normalizers/jsonapi-normalizer.ts';
import { normalizeUrl } from '../normalizers/url-normalizer.ts';

/**
 * A wrapped JSON:API response.
 */
export interface WrappedResponse {
  content?: JsonApiDocument;
}

export function isWrappedJsonApiDocument(
  response: unknown
): response is WrappedResponse {
  // Handle both direct objects and wrapped responses
  const wrapped = response as WrappedResponse;
  const data = wrapped?.content || (response as JsonApiDocument);

  return (
    typeof data === 'object' &&
    data !== null &&
    ('data' in data || 'included' in data)
  );
}

export function isJsonApiDocument(
  document: unknown
): document is JsonApiDocument {
  return (
    typeof document === 'object' &&
    document !== null &&
    ('data' in document || 'included' in document)
  );
}

const camelCaseToDasherizeNormalizer = new NormalizeCamelCaseToDasherize();
const dasherizeToCamelCaseNormalizer = new NormalizeDasherizeToCamelCase();

/**
 * Handler that normalizes JSON:API type names between camelCase (API) and kebab-case (WarpDrive).
 *
 * The API uses camelCase type names (e.g., "mediaImages"),
 * but WarpDrive schemas are registered with kebab-case names (e.g., "media-images").
 *
 * This handler:
 * 1. Transforms outgoing URLs from kebab-case to camelCase for the API
 * 2. Transforms incoming response types from camelCase to kebab-case for WarpDrive
 */
export class TypeNormalizerHandler implements Handler {
  async request<T = unknown>(
    context: RequestContext,
    next: NextFn<T>
  ): Promise<T | StructuredDataDocument<T>> {
    if (!isJsonApiRequest(context.request)) {
      return next({ ...context.request });
    }

    // Transform the outgoing request URL from kebab-case to camelCase
    const modifiedContext = this.normalizeRequest(context);

    // Execute the request
    const response = await next({ ...modifiedContext.request });

    // Transform the incoming response types from camelCase to kebab-case
    if (
      isJsonApiDocument(response.content) ||
      isWrappedJsonApiDocument(response)
    ) {
      this.normalizeResponse(response);
    } else {
      console.error(
        'TypeNormalizerHandler: Response is not a JSON:API document',
        response
      );
    }

    return response;
  }

  /**
   * Transforms URLs containing kebab-case type names to camelCase for the API.
   * Example: /api/media-images -> /api/mediaImages
   */
  private normalizeRequest(context: RequestContext): RequestContext {
    if (!context.request.url) {
      return context;
    }

    const method = context.request.method;
    const url = normalizeUrl(context.request.url);

    if (method === 'POST' || method === 'PUT' || method === 'PATCH') {
      const parsedBody = JSON.parse(
        context.request.body as string
      ) as JsonApiDocument;
      if (isJsonApiDocument(parsedBody)) {
        dasherizeToCamelCaseNormalizer.normalizeDocument(parsedBody);
        return {
          ...context,
          request: {
            ...context.request,
            url,
            body: JSON.stringify(parsedBody),
          },
        };
      }
    }

    return {
      ...context,
      request: {
        ...context.request,
        url,
      },
    };
  }

  private normalizeResponse(response: unknown): void {
    // Handle both direct objects and wrapped responses
    const wrapped = response as WrappedResponse;
    const document = wrapped?.content || (response as JsonApiDocument);
    camelCaseToDasherizeNormalizer.normalizeDocument(document);
  }
}
